// Contact forms of the marketing site (tallerza.cl): "Agenda una demo" and
// "Lista de espera". The same schema validates in the browser and in the
// `contacto` Function (Admin SDK bypasses Firestore rules, so the server-side
// check is the real one). Messages come from the landing handoff (COPY.md).
import * as z from 'zod/mini';
import { formatearRut, validarRut } from './rut.ts';

export const TIPOS_TALLER = [
  'desabolladura_pintura',
  'mecanica',
  'neumaticos_lubricentro',
  'electrica_electronica',
  'otro',
] as const;
export type TipoTaller = (typeof TIPOS_TALLER)[number];

export const ETIQUETAS_TIPO_TALLER: Record<TipoTaller, string> = {
  desabolladura_pintura: 'Desabolladura y pintura',
  mecanica: 'Mecánica',
  neumaticos_lubricentro: 'Neumáticos y lubricentro',
  electrica_electronica: 'Eléctrica y electrónica',
  otro: 'Otro',
};

export const TAMANOS_EQUIPO = ['1-2', '3-10', '11-30', '30+'] as const;
export type TamanoEquipo = (typeof TAMANOS_EQUIPO)[number];

export const ETIQUETAS_TAMANO: Record<TamanoEquipo, string> = {
  '1-2': '1–2',
  '3-10': '3–10',
  '11-30': '11–30',
  '30+': 'Más de 30',
};

/**
 * Status of a lead (`contactos/{id}.estado`), the sales funnel of the
 * business plan (§7.4: CAC per campaign = spend ÷ clients). The `contacto`
 * Function creates it as 'nuevo'; the superadmin moves it among
 * ESTADOS_CONTACTO_MANUALES directly (firestore.rules): contacted, demo done,
 * in pilot, lost (with an optional reason) or discarded (spam, not a
 * workshop: never an opportunity). 'cliente' is final and only the
 * `convertirContacto` callable sets it, because that is when a coupon's use
 * is counted.
 */
export const ESTADOS_CONTACTO = [
  'nuevo',
  'contactado',
  'demo',
  'piloto',
  'cliente',
  'perdido',
  'descartado',
] as const;
export type EstadoContacto = (typeof ESTADOS_CONTACTO)[number];

/** The states a client may write (rules); never 'cliente', never from it. */
export const ESTADOS_CONTACTO_MANUALES = [
  'nuevo',
  'contactado',
  'demo',
  'piloto',
  'perdido',
  'descartado',
] as const;
export type EstadoContactoManual = (typeof ESTADOS_CONTACTO_MANUALES)[number];

/** Still an open opportunity (the Contactos page's default filter). */
export const ESTADOS_CONTACTO_ABIERTOS = ['nuevo', 'contactado', 'demo', 'piloto'] as const;

export const ETIQUETAS_ESTADO_CONTACTO: Record<EstadoContacto, string> = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  demo: 'Demo',
  piloto: 'Prueba gratis',
  cliente: 'Cliente',
  perdido: 'Perdido',
  descartado: 'Descartado',
};

/**
 * Optional reason of a lost lead (`contactos/{id}.motivoPerdida`), only
 * while its status is 'perdido' (the rules enforce both).
 */
export const MAX_MOTIVO_PERDIDA = 200;

/**
 * Retention of a lead that never became a customer. The privacy notice
 * (apps/landing/src/pages/privacidad.astro) promises deletion "dentro de 12
 * meses desde el último contacto". A Firestore TTL policy on
 * `contactos.expiraEn` deletes the document once that Timestamp passes; TTL
 * deletes "typically within 24 hours" after it, so the deadline keeps a week
 * of margin under a year (365 − 7 days).
 *
 * Writers (always the server, Timestamp): `contacto` on every form sent and
 * `invitarAPrueba` on every invitation or resend (both are a contact with the
 * person) set `ahora + RETENCION_CONTACTO_MS`; `convertirContacto` removes it
 * (a customer's data moves to its account and is kept). Without the field the
 * TTL never deletes the document.
 */
export const RETENCION_CONTACTO_DIAS = 358;
export const RETENCION_CONTACTO_MS = RETENCION_CONTACTO_DIAS * 86_400_000;

/** Server field of `contactos/{id}` that drives the TTL (absent once a customer). */
export type RetencionContacto<F = unknown> = { expiraEn?: F };

/** Length limits of the demo form's optional company data. */
export const MAX_RAZON_SOCIAL = 120;
export const MAX_DIRECCION = 200;

/** Anti-spam field: hidden from people, must arrive empty or absent. */
export const CAMPO_HONEYPOT = 'sitio_web';

export const MENSAJES_CONTACTO = {
  requerido: 'Completa este campo.',
  correo: 'Revisa el correo, parece que falta algo.',
  whatsapp: 'Ingresa los 8 dígitos de tu celular, por ejemplo 1234 5678.',
  envio: 'No pudimos enviar tus datos. Inténtalo de nuevo o escríbenos por WhatsApp.',
  // Not in COPY.md: only reachable past the inputs' maxlength.
  largo: 'Este campo es demasiado largo.',
  cupon: 'Este código no es válido o ya venció.',
  rut: 'Revisa el RUT, por ejemplo 76.123.456-0.',
} as const;

const M = MENSAJES_CONTACTO;

/**
 * Chilean mobile number → "+569XXXXXXXX", or null. Accepts it with or
 * without +56 / 56 and with spaces, dashes, dots or parentheses, or just the
 * 8 digits after "+56 9" (the site's field shows that prefix fixed).
 */
export function normalizarWhatsapp(s: string): string | null {
  const limpio = s.trim().replace(/[\s\-().]/g, '');
  const m = /^(?:(?:\+?56)?9)?(\d{8})$/.exec(limpio);
  return m ? `+569${m[1]}` : null;
}

/** '' or whitespace → undefined, so an empty optional form field is absent. */
const opcional = <T extends z.ZodMiniType>(esquema: T) =>
  z.pipe(
    z.transform((v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v)),
    z.optional(esquema),
  );

const texto = (min: number, max: number) =>
  z
    .string({ error: M.requerido })
    .check(
      z.trim(),
      z.minLength(min, { error: M.requerido }),
      z.maxLength(max, { error: M.largo }),
    );

const correo = z.pipe(
  z
    .string({ error: M.requerido })
    .check(
      z.trim(),
      z.minLength(1, { error: M.requerido }),
      z.maxLength(120, { error: M.correo }),
      z.toLowerCase(),
    ),
  z.email({ error: M.correo }),
);

const whatsapp = z.pipe(
  z
    .string({ error: M.requerido })
    .check(
      z.trim(),
      z.minLength(1, { error: M.requerido }),
      z.maxLength(30, { error: M.whatsapp }),
    ),
  // Only reached when the length checks pass (a pipe stops on issues).
  z.string().check(
    z.refine((s) => normalizarWhatsapp(s) !== null, { error: M.whatsapp }),
    z.overwrite((s) => normalizarWhatsapp(s) ?? s),
  ),
);

const tipoTaller = z.enum(TIPOS_TALLER, { error: M.requerido });

/** Slug of the landing section the form was sent from (analytics only). */
const rubroOrigen = opcional(
  z.string().check(z.regex(/^[a-z0-9-]{1,40}$/, { error: M.requerido })),
);

/**
 * Company RUT (optional in the demo form): checked with módulo 11 and stored
 * formatted ("76.123.456-0"), as the workshop's `empresa.rut` expects it.
 */
const rut = z.pipe(
  z.string({ error: M.rut }).check(z.trim(), z.maxLength(20, { error: M.rut })),
  // Only reached when the length check passes (a pipe stops on issues).
  z.string().check(z.refine(validarRut, { error: M.rut }), z.overwrite(formatearRut)),
);

/** Coupon code as stored: `cupones/{CODIGO}` (so no '/' can reach a path). */
export const REGEX_CODIGO_CUPON = /^[A-Z0-9-]{3,20}$/;

/** Trims and uppercases a typed code; the result may still be invalid. */
export const normalizarCodigoCupon = (s: string) => s.trim().toUpperCase();

/** Valid normalized code, or null. */
export function codigoCupon(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  const c = normalizarCodigoCupon(s);
  return REGEX_CODIGO_CUPON.test(c) ? c : null;
}

const cupon = opcional(
  z
    .string({ error: M.cupon })
    .check(z.overwrite(normalizarCodigoCupon), z.regex(REGEX_CODIGO_CUPON, { error: M.cupon })),
);

/**
 * Attribution text (UTM values): trimmed, control characters dropped and cut
 * to `max`. Never an error: a malformed campaign tag must not cost a lead.
 */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/g;

const atribucion = (max: number) =>
  z.pipe(
    z.transform((v: unknown) => {
      if (typeof v !== 'string') return undefined;
      const limpio = v.replace(CONTROL, '').trim().slice(0, max).trim();
      return limpio === '' ? undefined : limpio;
    }),
    z.optional(z.string()),
  );

/**
 * Site path the form was sent from ("/", "/desabolladura-y-pintura"), without
 * query or fragment; anything else is dropped (analytics only, never an error).
 */
const pagina = z.pipe(
  z.transform((v: unknown) => {
    if (typeof v !== 'string') return undefined;
    const ruta = v.trim().split(/[?#]/)[0] ?? '';
    return /^\/[A-Za-z0-9\-._~/%]{0,99}$/.test(ruta) ? ruta : undefined;
  }),
  z.optional(z.string()),
);

/** Optional fields shared by both forms: coupon, origin page and UTM tags. */
const origen = {
  cupon,
  pagina,
  utm_source: atribucion(60),
  utm_medium: atribucion(60),
  utm_campaign: atribucion(60),
};

export const esquemaDemo = z.object({
  tipo: z.literal('demo'),
  nombre: texto(2, 80),
  taller: texto(2, 100),
  whatsapp,
  correo: opcional(correo),
  tipoTaller,
  tamano: opcional(z.enum(TAMANOS_EQUIPO, { error: M.requerido })),
  // Company data, optional: lets the superadmin prefill the trial invitation
  // (same names and limits as `esquemaInvitarAPrueba`).
  razonSocial: opcional(texto(1, MAX_RAZON_SOCIAL)),
  rut: opcional(rut),
  direccion: opcional(texto(1, MAX_DIRECCION)),
  rubroOrigen,
  ...origen,
});

export const esquemaEspera = z.object({
  tipo: z.literal('espera'),
  correo,
  tipoTaller,
  rubroOrigen,
  ...origen,
});

export const esquemaContacto = z.discriminatedUnion('tipo', [esquemaDemo, esquemaEspera], {
  error: M.requerido,
});

export type ContactoDemo = z.infer<typeof esquemaDemo>;
export type ContactoEspera = z.infer<typeof esquemaEspera>;
export type Contacto = z.infer<typeof esquemaContacto>;

const mensajesConocidos = new Set<string>(Object.values(M));

/** First error of each field, as the message to show under it. */
export function erroresPorCampo(error: z.core.$ZodError): Record<string, string> {
  const errores: Record<string, string> = {};
  for (const issue of error.issues) {
    const campo = String(issue.path[0] ?? 'formulario');
    if (campo in errores) continue;
    errores[campo] = mensajesConocidos.has(issue.message) ? issue.message : M.requerido;
  }
  return errores;
}
